import { Injectable } from "@angular/core";
import { Actions, concatLatestFrom, createEffect, ofType } from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { ProductType } from "@obg/core.config";
import { ActionWithPayload } from "@obg/ngrx-utils";
import { Log, LoggingFactory } from "@obg/ngx.global.evo";
import { RouteAction, RouteSelector } from "@obg/ngx.router";
import { isEmpty } from "lodash-es";
import { map, skip, startWith, switchMap, tap } from "rxjs/operators";

import { AppContextSelector } from "../app-context/app-context.selector";
import { PageAction } from "../page/page.action";
import { Page, PageData } from "../page/page.model";
import { CoreState } from "../state";

import { DOCUMENT_META_DATA_ACTION_TYPE, DocumentMetaAction } from "./document-meta.action";
import { DocumentMetaData, MetaField, MetaFields, OverrideData, UpdateCanonicalPayload } from "./document-meta.model";
import { DocumentMetaService } from "./document-meta.service";

@Injectable()
export class DocumentMetaEffect {
	unsetMetaFields$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(RouteAction.resolveEnd),
				map(() => this.service.clearTagsOnNavigation())
			),
		{ dispatch: false }
	);

	documentMetaType$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PageAction.setCurrent),
			map(({ payload }) => payload),
			tap((pageData) => {
				if (isEmpty(pageData)) {
					this.logger.error("documentMetaType$", "Page data is missing");
				}
			}),
			concatLatestFrom(() => [
				this.store.select(RouteSelector.getData),
				this.store.select(AppContextSelector.getProduct),
			]),
			switchMap((params) =>
				this.store.pipe(
					select(RouteSelector.getQueryParams),
					// If on desktop we click on different events the queryParam emits when we remove the value and when we add the new one
					// On page navigation it emits only once, that's why this return value will be false on the first emit but true after that one
					skip(1),
					map(() => true),
					startWith(false),
					map((isQueryParamsChangeOnly) => [...params, isQueryParamsChangeOnly])
				)
			),
			map(
				([pageData, routeData, product, isQueryParamsChangeOnly]: [
					PageData,
					OverrideData,
					ProductType | undefined,
					boolean,
				]) => {
					if (routeData?.shouldOverrideDocumentMeta) {
						return this.action.initializeOverride(isQueryParamsChangeOnly);
					}

					// TODO: Move logic related to gaming document-meta.effect Ref #AOCC-998
					const pageDataClone = { ...pageData, page: { ...pageData.page } };
					if (!pageDataClone.page.seoText) {
						pageDataClone.page.seoText = this.getAltSeoText(pageDataClone.page, product);
					}

					const documentMetaData = this.service.mapPageToDocumentMetaData(
						pageDataClone,
						routeData.customMetaData ?? false
					);
					return this.action.set(documentMetaData);
				}
			)
		)
	);

	setDocumentMeta$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(DOCUMENT_META_DATA_ACTION_TYPE.set),
				map((x: ActionWithPayload<DocumentMetaData>) => x.payload),
				tap((documentMetaData) => this.service.updateDocumentMeta(documentMetaData))
			),
		{ dispatch: false }
	);

	updateMetaTitle$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(DOCUMENT_META_DATA_ACTION_TYPE.updateMetaTitle),
				map((x: ActionWithPayload<string>) => x.payload),
				tap((metaTitle) => this.service.updateMetaTitle(metaTitle))
			),
		{ dispatch: false }
	);

	updateCanonical$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(DOCUMENT_META_DATA_ACTION_TYPE.updateCanonical),
				map((x: ActionWithPayload<UpdateCanonicalPayload>) => x.payload),
				tap((payload) => this.service.updateCanonicalLink(payload.url, payload.addLocationPrefix))
			),
		{ dispatch: false }
	);

	// Only use in conjuction with customPageMeta on router, otherwise you risk having race conditions.
	setMetaData$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(DOCUMENT_META_DATA_ACTION_TYPE.setMetaData),
				map((x: ActionWithPayload<MetaFields>) => x.payload),
				tap((metaFields: MetaFields) => {
					const fixedMetaFields: MetaFields = metaFields.map(
						(metaField: MetaField) =>
							({
								...metaField,
								clearOnNavigation: true,
							}) as MetaField
					);
					this.service.setMetaElements(fixedMetaFields);
				})
			),
		{ dispatch: false }
	);

	setCustomMetaDataFromConfig$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(DOCUMENT_META_DATA_ACTION_TYPE.setMetaDataFromConfig),
				map(() => this.service.createCustomMetaTagsFromConfig())
			),
		{ dispatch: false }
	);

	private logger: Log;

	constructor(
		private actions$: Actions,
		private service: DocumentMetaService,
		private action: DocumentMetaAction,
		private store: Store<CoreState>,
		private translateService: TranslateService,
		loggingFactory: LoggingFactory
	) {
		this.logger = loggingFactory.get("documentMetaEffect");
	}

	private getAltSeoText(page: Page, product?: ProductType): string | undefined {
		const { documentKey, seoText } = page;
		const gameCollections = [
			ProductType.Casino,
			ProductType.LiveCasino,
			ProductType.VirtualSports,
			ProductType.Poker,
			ProductType.Jackpots,
			ProductType.Slots,
		];

		if (product && gameCollections.includes(product)) {
			const gameCollectionCrsKey = `game-collections.${documentKey}.SeoText`;
			const seoRichText = this.translateService.instant(gameCollectionCrsKey);
			if (seoRichText !== gameCollectionCrsKey) {
				return seoRichText;
			}
		}

		return seoText;
	}
}
